import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

const IMG_BASE = "/assets/project/UAS Based Medical Drone";

const Figure = ({ src, alt, caption }) => (
  <figure className={styles.figureCard}>
    <img src={src} alt={alt} className={styles.galleryImage} />
    <figcaption className={styles.galleryCaption}>
      {caption}
    </figcaption>
  </figure>
);

export default function MedicalDeliveryUAVPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          UAS-Based Logistics Network for Critical Medical Delivery
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Research Assistant)</span>
        </div>
        <div className={styles.affiliations}>
          Supervised by: Dr. Md. Sakir Hossain (Asst. Prof, Dept. of Avionics Engineering, AAUB)
        </div>
        <div className={styles.badge} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#047857', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          💰 RIC World Bank Initiative — BDT 55 Lakh Sanctioned
        </div>
        <div className={styles.headerActions}>
          <a
            href="https://shorturl.at/RK2G6"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#047857',
              textDecoration: 'none',
              background: 'rgba(16, 185, 129, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            📄 Part 1: Proposal →
          </a>
          <a
            href="https://shorturl.at/kWNsk"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#047857',
              textDecoration: 'none',
              background: 'rgba(16, 185, 129, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            🎤 Part 2: Presentation →
          </a>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          During severe flooding and natural disasters in Bangladesh, remote communities often lose access to life-saving medical supplies. To address this critical infrastructure gap, I served as the UAV Design Engineer to conceptualize and design an Unmanned Aerial System (UAS) logistics network. The research proposal was accepted by the <strong>Research Innovation Center (RIC)</strong> with a sanctioned fund of <strong>BDT 55 lakh</strong>, supported under the <strong>World Bank initiative</strong> in Bangladesh. We successfully completed the initial conceptual, preliminary, and detailed design phases. However, following the political changes during the July 2024 uprising and subsequent change of government, all ongoing project funding was temporarily suspended due to the national economic crisis. Despite these challenges, our project remains under active development with <strong>Frontier Alliance</strong>.
        </p>
        <div className={styles.keywords}>
          <strong>Focus Areas:</strong> UAV Design, Systems Engineering, Payload Analysis, Project Budgeting, Humanitarian Logistics, Disaster Management.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>The Problem</h2>
        <p className={styles.paragraph}>
          Studies and reports consistently highlight the challenges faced by remote communities in accessing essential healthcare services and supplies due to geographical barriers and underdeveloped infrastructure. In rural Bangladesh, <strong>snakebite deaths</strong> pose a significant economic burden, particularly affecting farmers, who are often the prime victims. Reports show snakebite kills around <strong>7,500 people every year</strong>.
        </p>
        <p className={styles.paragraph}>
          With limited access to timely medical intervention, these incidents not only result in a tragic loss of life but also incur substantial economic costs to affected families and communities. During monsoon flooding, road networks are severed for days or weeks, making conventional ambulance and delivery services impossible.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>The Solution</h2>
        <p className={styles.paragraph}>
          Our proposed <strong>UAS-based system</strong> directly addresses these critical access challenges. By deploying autonomous medical delivery drones, we can significantly reduce travel times for life-saving medicines and blood units, enabling faster diagnoses, improved treatment adherence, and ultimately better health outcomes.
        </p>
        <p className={styles.paragraph}>
          The system is designed to operate in degraded infrastructure environments, contributing directly to Bangladesh's disaster management framework. Beyond snakebite antivenom, the platform can transport emergency pharmaceuticals, diagnostic samples, and critical supplies to flood-prone regions where traditional logistics fail.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Mission Requirements & Payload Analysis</h2>
        <p className={styles.paragraph}>
          Designing an aircraft for disaster response requires strict adherence to mission profiles. The UAV was optimized to transport critical medical payloads — such as antivenom, blood units, and emergency pharmaceuticals — to isolated regions where road networks are impassable.
        </p>
        <p className={styles.paragraph}>
          I conducted comprehensive payload sizing and aerodynamic efficiency calculations to ensure the UAV could successfully carry a dense payload while maintaining steady cruise performance. This required precise battery weight-to-energy density analysis and structural material selection to maximize endurance without compromising structural integrity.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>5 kg</div>
            <div className={styles.metricLabel}>Payload Capacity</div>
            <p className={styles.metricSubtext}>
              Optimized for dense medical supplies and blood units.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>100 km</div>
            <div className={styles.metricLabel}>Operational Range</div>
            <p className={styles.metricSubtext}>
              Ensures reach into deeply flooded and remote terrains.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>BDT 55L</div>
            <div className={styles.metricLabel}>Sanctioned Funding</div>
            <p className={styles.metricSubtext}>
              RIC World Bank initiative for prototype manufacturing.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Engineering Design & Project Timeline</h2>
        <p className={styles.paragraph}>
          I led the transition from preliminary sizing to detailed design, creating the complete manufacturing budget and bill of materials (BOM) for the prototype. The design process progressed through three rigorous phases:
        </p>
        <ul className={styles.bullets}>
          <li><strong>Conceptual Design:</strong> Mission profile definition, payload trade studies, and configuration selection.</li>
          <li><strong>Preliminary Design:</strong> Aerodynamic sizing, propulsion selection, weight estimation, and stability analysis.</li>
          <li><strong>Detailed Design:</strong> Structural modeling, payload deployment mechanism, avionics integration, and manufacturing BOM.</li>
        </ul>
        <p className={styles.paragraph}>
          The image below demonstrates the structural modeling and layout configurations utilized during the detailed design phase to ensure safe payload deployment mechanisms.
        </p>

        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/1.png`}
            alt="UAV Structural & Payload Design"
            caption="Figure 1: Structural modeling and payload layout configuration from the detailed design phase."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Funding Status & Current Development</h2>
        <p className={styles.paragraph}>
          The Research Innovation Center (RIC) confirmed that the allocated funds were scheduled to be disbursed in the first week of August 2024. However, following the political changes during the <strong>July 2024 uprising</strong> and subsequent change of government, all ongoing project funding was temporarily suspended due to the national economic crisis.
        </p>
        <p className={styles.paragraph}>
          Despite these setbacks, the project remains under <strong>active development with Frontier Alliance</strong>. We continue refining the airframe design, propulsion system, and autonomous navigation stack in preparation for future funding opportunities and field deployment.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <a
            href="https://shorturl.at/RK2G6"
            target="_blank"
            rel="noreferrer"
            className={styles.btnPrimary}
            style={{ background: '#047857', borderColor: '#047857' }}
          >
            📄 Part 1: Project Proposal
          </a>
          <a
            href="https://shorturl.at/kWNsk"
            target="_blank"
            rel="noreferrer"
            className={styles.btnPrimary}
            style={{ background: '#047857', borderColor: '#047857' }}
          >
            🎤 Part 2: Presentation & Budget
          </a>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This project demonstrates the intersection of aerospace engineering and humanitarian logistics. By successfully mapping mission requirements to aerodynamic capabilities, defending a comprehensive manufacturing budget, and securing institutional funding, the project proved that advanced UAV networks are a viable, cost-effective solution for disaster management in developing nations. Even amid funding suspension, the continued development through Frontier Alliance reflects a commitment to delivering life-saving technology to the communities who need it most.
        </p>
      </section>

    </div>
  );
}